import React, { useState, useEffect } from "react";
import { styled } from "@mui/styles";
import useTitle from "utils/custom-hooks/useTitle";
import theme from "theme/default";
import {
  borderRadius,
  boxShadows,
  colors,
  fontSize,
  fontWeight,
  gradients,
  margin,
  padding,
} from "theme/defaultStyle";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import GradientIcon from "theme/icons/GradientIcon";
import { TabNavigatorItem } from "entities/UIModel";
import { PERMISSIONS } from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
// import SerialNumberAssignIcon from "theme/icons/SerialNumberAssignIcon";
import InventoryAssignNew from "pages/assign/new/InventoryAssignNew";
import InventoryAssign from "pages/assign/assignGrid/InventoryAssign";
import InventoryLookup from "pages/lookup/InventoryLookup";
import InventoryModify from "pages/modify/modifyGrid/InventoryModify";
import InventoryModifyNew from "pages/modify/new/InventoryModifyNew";
import InventoryShareNew from "pages/share/new/InventorySharedNew";
import InventoryShareShared from "pages/share/InventoryShareShared";
import SerialNumberLookupIcon from "theme/icons/SerialNumberLookupIcon";
import ActionButton from "controls/global/action-button";
import InventoryVerifyDialog from "pages/lookup/inventory-verify/InventoryVerifyDialog";
import usePolicyStore from "utils/context/InventoryPolicyContext";

const NavigationContainer = styled(Box)({
  background: "transparent",
  // marginLeft: "9px",
  padding: padding.zero,
  [theme.breakpoints.up("sm")]: {
    padding: padding.zero,
  },
  "& .MuiTabs-root": {
    height: "49px",
    marginTop: margin.zero,
    minHeight: "49px",
    "& .MuiTabs-flexContainer": {
      marginTop: margin.zero,
    },
  },
  "& .tabPanel": {
    backgroundColor: colors.white,
    borderRadius: `0 ${borderRadius.xlarge} ${borderRadius.xlarge} ${borderRadius.xlarge}`,
    boxShadow: boxShadows.boxShadow05,
    margin: margin.zero,
    "& > .MuiBox-root": {
      padding: "24px",
    },
  },
  "& .tab": {
    "&.MuiButtonBase-root.MuiTab-root": {
      backgroundColor: colors.grey10,
    },
  },
});

const SubNavigationContainer = styled(Box)({
  background: theme.palette.background.paper,
  padding: padding.large1,
  marginTop: margin.xxsmall1,
  // marginLeft: margin.small1,
});

const StyledTabs = styled(Tabs)({
  borderRadius: `${borderRadius.large} ${borderRadius.large} 0 0`,
  boxShadow: boxShadows.boxShadow04,
  height: 48,
  marginBottom: margin.zero,
  marginLeft: margin.zero,
  marginRight: margin.zero,
  marginTop: margin.xsmall1,
  width: "fit-content",

  "& .MuiTabs-scroller": {
    background: colors.grey10,
  },

  "& .MuiTab-root": {
    color: "#215D80",
    fontSize: fontSize.base,
    fontWeight: fontWeight.bold1,
    opacity: 1,
    flexDirection: "row",
  },

  "& .MuiTab-root.Mui-selected": {
    color: "#215D80",
    backgroundColor: colors.white,
    paddingLeft: padding.xlarge11,
  },

  "& .MuiSvgIcon-root": {
    color: "#215D80",
    marginRight: margin.xsmall,
  },

  "& .MuiTab-root:first-of-type": {
    borderRadius: `${borderRadius.large} 0 0 0`,
  },
  "& .MuiTab-root:last-of-type": {
    borderRadius: `0 ${borderRadius.large} 0 0`,
  },
});

const Container = styled("div")({
  backgroundColor: theme.palette.background.default,
  borderBottomLeftRadius: borderRadius.xlarge,
  borderBottomRightRadius: borderRadius.xlarge,
  position: "relative",
  "& .header": {
    display: "flex",
    alignItems: "center",
  },
  "& .headerContainer": {
    display: "flex",
    alignItems: "center",
    height: "28px",
    margin: "40px 0px 20px 0px",
    justifyContent: "space-between",
  },
  "& .titleQuickReferenceCard": {
    // marginLeft: "10px",
    background: gradients.redGradient03,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: fontWeight.bold2,
    fontSize: fontSize.xlarge2,
    paddingLeft: padding.small1,
  },
});

const InventoryAssignContainer = styled(Box)({
  padding: padding.large1,
  borderRadius: `${borderRadius.zero} ${borderRadius.xlarge} ${borderRadius.xlarge} ${borderRadius.xlarge}`,
});

const ButtonContainer = styled(Box)({
  padding: padding.xsmall1,
  marginBottom: margin.small1,
  marginLeft: margin.small2,
});

interface Props {
  saveFileRef: any;
  saveFileChangeRef: any;
  tabSaveChangesRef: any;
}

enum ShareTabKey {
  New = "new",
  Shared = "shared",
}

const PolicyInventoryHome = ({ saveFileRef, saveFileChangeRef, tabSaveChangesRef }: Props) => {
  useTitle("Stewart Connect - Policy Inventory");
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  // const [, { refresh },] = useLookupStore();
  const [, { setActiveTab }] = usePolicyStore();

  const [openInventoryVerifyDialog, setOpenInventoryVerifyDialog] = useState(false);

  const hasInventoryVerifyAccess = permissionCodes.includes(PERMISSIONS.INV_VERIFY_ACCESS);
  const [assignTabIndex, setAssignTabIndex] = useState(0);
  const [modifyTabIndex, setModifyTabIndex] = useState(0);
  const [shareTabIndex, setShareTabIndex] = useState(0);

  function getTabNames(): TabNavigatorItem[] {
    let reorderedTabs: TabNavigatorItem[] = [];
    if (permissionCodes?.includes(PERMISSIONS.INV_LOOKUP_VIEW)) {
      reorderedTabs.push({ title: "LOOKUP", disabled: false });
    }
    if (permissionCodes?.includes(PERMISSIONS.INV_ASSIGN_VIEW)) {
      reorderedTabs.push({ title: "ASSIGN", disabled: false });
    }
    if (permissionCodes?.includes(PERMISSIONS.INV_MODIFY_VIEW)) {
      reorderedTabs.push({ title: "MODIFY", disabled: false });
    }
    if (
      permissionCodes?.includes(PERMISSIONS.INV_SHARE_VIEW) ||
      permissionCodes?.includes(PERMISSIONS.INV_SHARE_MODIFY)
    ) {
      reorderedTabs.push({ title: "SHARE", disabled: false });
    }
    return reorderedTabs;
  }

  const tabs = getTabNames();

  function getDefaultTabIndex() {
    if (
      permissionCodes?.includes(PERMISSIONS.INV_LOOKUP_VIEW) &&
      permissionCodes?.includes(PERMISSIONS.INV_MODIFY_VIEW) &&
      permissionCodes?.includes(PERMISSIONS.INV_ASSIGN_VIEW)
    ) {
      const idx = tabs.findIndex((tab) => tab.title === "LOOKUP");
      return idx !== -1 ? idx : 0;
    } else if (permissionCodes?.includes(PERMISSIONS.INV_ASSIGN_VIEW)) {
      const idx = tabs.findIndex((tab) => tab.title === "ASSIGN");
      return idx !== -1 ? idx : 0;
    } else if (permissionCodes?.includes(PERMISSIONS.INV_MODIFY_VIEW)) {
      const idx = tabs.findIndex((tab) => tab.title === "MODIFY");
      return idx !== -1 ? idx : 0;
    } else if (
      permissionCodes?.includes(PERMISSIONS.INV_SHARE_VIEW) ||
      permissionCodes?.includes(PERMISSIONS.INV_SHARE_MODIFY)
    ) {
      const idx = tabs.findIndex((tab) => tab.title === "SHARE");
      return idx !== -1 ? idx : 0;
    }

    return 0;
  }
  const [tabIndex, setTabIndex] = useState(0);

  // Update tab index if permissions change
  useEffect(() => {
    setTabIndex(getDefaultTabIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setAssignTabIndex(0);
    setModifyTabIndex(0);
    setShareTabIndex(0);
  };

  const showNewAssignTab = permissionCodes?.includes(PERMISSIONS.INV_ASSIGN_MODIFY);
  useEffect(() => {
    if (!showNewAssignTab && assignTabIndex === 0) setAssignTabIndex(0);
  }, [showNewAssignTab]);

  const showNewSharedTab = permissionCodes?.includes(PERMISSIONS.INV_SHARE_MODIFY);
  const showSharedListTab = permissionCodes?.includes(PERMISSIONS.INV_SHARE_VIEW);
  useEffect(() => {
    if (!showNewSharedTab && shareTabIndex === 0) setShareTabIndex(0);
  }, [showNewSharedTab]);

  const showNewModifyTab =
    permissionCodes?.includes(PERMISSIONS.INV_TRANSFER_MODIFY) ||
    permissionCodes?.includes(PERMISSIONS.INV_EDIT_MODIFY);

  useEffect(() => {
    if (!showNewModifyTab && modifyTabIndex === 0) setModifyTabIndex(0);
  }, [showNewModifyTab]);

  const renderTabPanel = () => {
    const tab = tabs[tabIndex] || tabs[0];
    if (!tab) return null;

    switch (tab.title) {
      case "LOOKUP":
        setActiveTab("LOOKUP");
        return (
          <Box mt={-1.5}>
            <InventoryLookup key={"assigned-data"} />
          </Box>
        );

      case "ASSIGN": {
        const options = [...(showNewAssignTab ? [{ label: "New" }] : []), { label: "Assigned" }];
        setActiveTab("ASSIGN");
        return (
          <SubNavigationContainer>
            <ButtonContainer>
              {options.map((option, idx) => (
                <Button
                  variant="text"
                  key={option.label}
                  onClick={() => setAssignTabIndex(idx)}
                  sx={{
                    backgroundColor: assignTabIndex === idx ? colors.blue18 : "",
                    fontWeight: assignTabIndex === idx ? fontWeight.bold1 : "normal",
                    textTransform: "none",
                    border: "none",
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonContainer>

            {showNewAssignTab && assignTabIndex === 0 && (
              <InventoryAssignContainer>
                <InventoryAssignNew
                  saveFileRef={saveFileRef}
                  saveFileChangeRef={saveFileChangeRef}
                  tabSaveChangesRef={tabSaveChangesRef}
                />
              </InventoryAssignContainer>
            )}
            {(!showNewAssignTab && assignTabIndex === 0) || (showNewAssignTab && assignTabIndex === 1) ? (
              <Box
                mt={-1.5}
                ml={-1}
              >
                <InventoryAssign />
              </Box>
            ) : null}
          </SubNavigationContainer>
        );
      }

      case "MODIFY": {
        const options = [...(showNewModifyTab ? [{ label: "New" }] : []), { label: "Modified" }];
        setActiveTab("MODIFY");
        return (
          <SubNavigationContainer>
            <ButtonContainer>
              {options.map((option, idx) => (
                <Button
                  variant="text"
                  key={option.label}
                  onClick={() => setModifyTabIndex(idx)}
                  sx={{
                    backgroundColor: modifyTabIndex === idx ? colors.blue18 : "",
                    textTransform: "none",
                    border: "none",
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonContainer>
            {showNewModifyTab && modifyTabIndex === 0 && (
              <InventoryAssignContainer>
                <InventoryModifyNew
                  saveFileRef={saveFileRef}
                  saveFileChangeRef={saveFileChangeRef}
                  tabSaveChangesRef={tabSaveChangesRef}
                />
              </InventoryAssignContainer>
            )}
            {(!showNewModifyTab && modifyTabIndex === 0) || (showNewModifyTab && modifyTabIndex === 1) ? (
              <Box
                mt={-1.5}
                ml={-1}
              >
                <InventoryModify />
              </Box>
            ) : null}
            {/* <StyledTabs
                            value={modifyTabIndex}
                            onChange={(_, newValue) => setModifyTabIndex(newValue)}
                            aria-label="Modify Nested Tabs"
                            sx={{ marginBottom: 2 }}
                        >
                            {nestedTabs.map((tab, idx) => (
                                <Tab key={tab.label} label={tab.label} />
                            ))}
                        </StyledTabs>
                        {showNewModifyTab && modifyTabIndex === 0 && (
                            <InventoryAssignContainer>
                                <InventoryModifyNew
                                    saveFileRef={saveFileRef}
                                    saveFileChangeRef={saveFileChangeRef}
                                    tabSaveChangesRef={tabSaveChangesRef}
                                />
                            </InventoryAssignContainer>

                        )}
                        {(!showNewModifyTab && modifyTabIndex === 0) || (showNewModifyTab && modifyTabIndex === 1) ? (
                            <Box mt={-1.5} ml={-1} >
                                <InventoryModify />
                            </Box>
                        ) : null} */}
          </SubNavigationContainer>
        );
      }
      case "SHARE": {
        const options = [
          ...(showNewSharedTab ? [{ label: "New", key: ShareTabKey.New }] : []),
          ...(showSharedListTab ? [{ label: "Shared", key: ShareTabKey.Shared }] : []),
        ];
        const selectedShareKey = options[shareTabIndex]?.key ?? options[0]?.key;
        return (
          <SubNavigationContainer>
            <ButtonContainer>
              {options.map((option, idx) => (
                <Button
                  variant="text"
                  key={option.key}
                  onClick={() => setShareTabIndex(idx)}
                  sx={{
                    backgroundColor: shareTabIndex === idx ? colors.blue18 : "",
                    fontWeight: shareTabIndex === idx ? fontWeight.bold1 : "normal",
                    textTransform: "none",
                    border: "none",
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonContainer>
            {selectedShareKey === ShareTabKey.New && (
              <InventoryAssignContainer>
                <InventoryShareNew />
              </InventoryAssignContainer>
            )}
            {selectedShareKey === ShareTabKey.Shared && (
              <Box
                mt={-1.5}
                ml={-1}
              >
                <InventoryShareShared />
              </Box>
            )}
          </SubNavigationContainer>
        );
      }

      default:
        return (
          <Box>
            <Typography>{tab.title}</Typography>
          </Box>
        );
    }
  };

  return (
    <Container>
      <Box className="headerContainer">
        <Box className="header">
          <GradientIcon Icon={SerialNumberLookupIcon} />
          <Typography
            className="titleQuickReferenceCard"
            variant="h6"
          >
            Policy Inventory
          </Typography>
        </Box>
        {tabs[tabIndex]?.title === "LOOKUP" && (
          <ActionButton
            variant="outlined"
            color="primary"
            disabled={!hasInventoryVerifyAccess}
            onClick={() => setOpenInventoryVerifyDialog(true)}
          >
            Verify Inventory
          </ActionButton>
        )}
        <InventoryVerifyDialog
          isOpen={openInventoryVerifyDialog}
          onClose={() => setOpenInventoryVerifyDialog(false)}
        />
      </Box>
      <NavigationContainer>
        <StyledTabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="Policy Inventory Tabs"
        >
          {tabs.map((tab, idx) => (
            <Tab
              key={tab.title}
              label={tab.title}
              disabled={tab.disabled}
              id={`policy-inventory-tab-${idx}`}
              aria-controls={`policy-inventory-tabpanel-${idx}`}
            />
          ))}
        </StyledTabs>
      </NavigationContainer>
      <Box style={{ marginTop: 0, paddingTop: 0 }}>{renderTabPanel()}</Box>
    </Container>
  );
};

export default PolicyInventoryHome;
